// MarriageNextStepLinks — cross-service link layer for /marriage/$nationality/in/$venue.
// Surfaces the 4 logical next-step services after registering a Thai-foreign marriage:
//   1. Affidavit notary (single-status / POA)
//   2. Certified translation of marriage certificate (into nationality's language)
//   3. MFA legalization OR Apostille (depending on country status)
//   4. Police clearance (for spouse visa applications abroad)
// Pages are 100% deterministic — no runtime data fetch.
// Phase: P-LINK-MARRIAGE-CROSS-SERVICE
import { Link } from "@tanstack/react-router";
import { ArrowRight, ScrollText, Languages, Stamp, ShieldCheck } from "lucide-react";
import type { MarriageNationality } from "@/data/marriage-nationalities";

// Map nationality region → most-relevant translation target language slug
// (matches src/data/translation-languages.ts slugs).
function targetLangSlug(n: MarriageNationality): string {
  const map: Record<string, string> = {
    american: "english",
    british: "english",
    canadian: "english",
    australian: "english",
    "new-zealander": "english",
    irish: "english",
    "south-african": "english",
    japanese: "japanese",
    korean: "korean",
    chinese: "chinese",
    taiwanese: "chinese",
    "hong-konger": "chinese",
    singaporean: "english",
    malaysian: "english",
    indian: "english",
    filipino: "english",
    indonesian: "english",
    vietnamese: "vietnamese",
    german: "german",
    french: "french",
    italian: "italian",
    spanish: "spanish",
    portuguese: "portuguese",
    dutch: "dutch",
    swedish: "swedish",
    norwegian: "norwegian",
    danish: "danish",
    finnish: "english",
    swiss: "german",
    austrian: "german",
    belgian: "french",
    polish: "polish",
    russian: "russian",
    ukrainian: "russian",
    czech: "english",
    israeli: "english",
    turkish: "turkish",
    saudi: "arabic",
    emirati: "arabic",
    egyptian: "arabic",
  };
  return map[n.slug] ?? "english";
}

interface Props {
  nationality: MarriageNationality;
  venueSlug: string;
  venueNameTh: string;
}

export function MarriageNextStepLinks({ nationality, venueSlug, venueNameTh }: Props) {
  const lang = targetLangSlug(nationality);
  // Marriage venue slugs don't always overlap the standard /locations registry —
  // a small whitelist of safe overlaps lets us deep-link into combo pages.
  const SHARED_LOC: Record<string, string> = {
    chiangmai: "hub-chiangmai-nimman",
    phuket: "hub-phuket-patong",
    pattaya: "hub-pattaya",
    huahin: "hub-huahin",
    kohsamui: "hub-kohsamui",
  };
  const sharedLoc = SHARED_LOC[venueSlug];

  const cards = [
    {
      to: sharedLoc ? `/notary/affidavit/in/${sharedLoc}` : "/notary/affidavit",
      icon: ScrollText,
      title: `Affidavit / Single-Status Notary`,
      blurb: "รับรอง Affidavit of Single Status, POA, สำเนาเอกสาร โดยทนายขึ้นทะเบียนสภาทนายความ — ส่งสถานทูตได้",
      accent: "from-amber-50 to-white border-amber-200 hover:border-amber-400",
      iconColor: "text-amber-700",
    },
    {
      to: sharedLoc ? `/translate/${lang}/in/${sharedLoc}` : `/translate/${lang}`,
      icon: Languages,
      title: `แปลทะเบียนสมรส (${nationality.countryEn})`,
      blurb: `แปลทะเบียนสมรสไทย ↔ ${nationality.countryTh} โดยนักแปลขึ้นทะเบียน MoJ/NAATI — ใช้ยื่นวีซ่าคู่สมรสได้`,
      accent: "from-emerald-50 to-white border-emerald-200 hover:border-emerald-400",
      iconColor: "text-emerald-700",
    },
    {
      to: nationality.apostilleOnly ? "/apostille" : "/services/mfa-legalization",
      icon: Stamp,
      title: nationality.apostilleOnly ? `Apostille (Hague)` : `รับรอง MFA / Legalization`,
      blurb: nationality.apostilleOnly
        ? `${nationality.countryTh}เป็นสมาชิก Hague Convention — รับรอง Apostille ใช้ได้ทั่วโลก 130 ประเทศ`
        : `รับรองกระทรวงการต่างประเทศไทย + สถานทูต${nationality.countryTh} ครบวงจรในแพ็กเดียว`,
      accent: "from-rose-50 to-white border-rose-200 hover:border-rose-400",
      iconColor: "text-rose-700",
    },
    {
      to: sharedLoc ? `/police-clearance/pcsc-thailand-local/in/${sharedLoc}` : "/police-clearance",
      icon: ShieldCheck,
      title: `Police Clearance (สำหรับวีซ่าคู่สมรส)`,
      blurb: `ตรวจประวัติอาชญากรรม สตช. + รับรองสถานทูต${nationality.countryTh} — เอกสารบังคับสำหรับวีซ่าคู่สมรสและ Green Card`,
      accent: "from-sky-50 to-white border-sky-200 hover:border-sky-400",
      iconColor: "text-sky-700",
    },
  ];

  return (
    <section className="border-t border-slate-200 bg-gradient-to-b from-slate-50/60 to-white py-12 sm:py-14">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-700">
          ขั้นตอนถัดไป · One-stop service
        </div>
        <h2 className="mt-3 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          หลังจดทะเบียนแล้ว ทำต่อที่ {venueNameTh} กับ NYC ครบจบที่เดียว
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          ทีมเดียวกัน — Notary, แปลเอกสาร, MFA/Apostille และ Police Clearance สำหรับวีซ่าคู่สมรส{nationality.countryEn}
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                className={`group flex items-start gap-3 rounded-2xl border bg-gradient-to-br ${c.accent} p-4 transition hover:-translate-y-0.5 hover:shadow-md`}
              >
                <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${c.iconColor}`} />
                <div className="flex-1">
                  <div className="font-display text-sm font-extrabold text-navy-deep">{c.title}</div>
                  <div className="mt-1 text-[12px] leading-snug text-slate-600">{c.blurb}</div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5" />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
